import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Title, useNotify, Button as RaButton, Link } from "react-admin";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate, formatIsoDate, MUI_MONTH_YEAR_PICKER_FORMAT } from "@/utils/date-format";
import { fromGregorian } from "@/utils/hijri-date-utils";
import type { FmbDashboardPeriod } from "@/containers/fmb/dashboard/components/FmbPeriodFilter";

type CalendarBooking = {
  id: string;
  bookedDate: string;
  performanceMode: "SELF" | "VAKALAT";
  fmbContributionId: string;
  fmbContribution?: {
    id: string;
    beneficiaryItsNo: string;
    beneficiaryName: string | null;
    quantity: number;
  };
  fmb?: { id: string; fileNo: string | null; itsNo: string | null; name: string | null } | null;
};

type DayPayload = {
  bookedDate: string;
  count: number;
  bookings: CalendarBooking[];
};

type CalendarPayload = {
  hijriYearStart: number;
  days: DayPayload[];
};

type AssignableContribution = {
  id: string;
  beneficiaryItsNo: string;
  beneficiaryName: string | null;
  quantity: number;
  assigned: number;
  remaining: number;
  fmb?: { fileNo: string | null; itsNo: string | null; name: string | null } | null;
};

function modeLabel(mode: string) {
  return mode === "VAKALAT" ? "Vakalat" : "Self";
}

function performanceModeSx(theme: Theme, mode: "SELF" | "VAKALAT") {
  const base = mode === "VAKALAT" ? theme.palette.secondary.main : theme.palette.info.main;
  return {
    color: base,
    bgcolor: alpha(base, 0.16),
  };
}

const PERFORMANCE_LEGEND: Array<{ mode: "SELF" | "VAKALAT"; label: string }> = [
  { mode: "SELF", label: "Self" },
  { mode: "VAKALAT", label: "Vakalat" },
];

function bookingLabel(b: CalendarBooking) {
  const name = b.fmbContribution?.beneficiaryName?.trim();
  const its = b.fmbContribution?.beneficiaryItsNo?.trim() || "—";
  const file = b.fmb?.fileNo ? ` · ${b.fmb.fileNo}` : "";
  const who = name ? `${name} · ${its}` : `ITS ${its}`;
  return `${who}${file}`;
}

/** Compact label for calendar day cells (name may truncate; ITS stays visible). */
function compactAssignmentParts(b: CalendarBooking) {
  return {
    name: b.fmbContribution?.beneficiaryName?.trim() || null,
    its: b.fmbContribution?.beneficiaryItsNo?.trim() || "—",
  };
}

const MAX_DAY_ASSIGNMENTS_VISIBLE = 3;

function assignableOptionLabel(c: AssignableContribution) {
  const name = c.beneficiaryName?.trim();
  const its = c.beneficiaryItsNo?.trim() || "—";
  const hofIts = c.fmb?.itsNo?.trim();
  const file = c.fmb?.fileNo?.trim();
  const parts = [
    name ? `${its} · ${name}` : `ITS ${its}`,
    file ? `file ${file}` : null,
    hofIts && hofIts !== its ? `HOF ${hofIts}` : null,
    `${c.remaining} left`,
  ].filter(Boolean);
  return parts.join(" · ");
}

function filterAssignableOptions(
  options: AssignableContribution[],
  inputValue: string
): AssignableContribution[] {
  const q = inputValue.trim().toLowerCase();
  if (!q) return options;
  return options.filter((c) => {
    const haystack = [
      c.beneficiaryItsNo,
      c.beneficiaryName,
      c.fmb?.itsNo,
      c.fmb?.fileNo,
      c.fmb?.name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

function FmbZabihatBookedSlotsList() {
  const theme = useTheme();
  const notify = useNotify();
  const [periods, setPeriods] = useState<FmbDashboardPeriod[]>([]);
  const [hijriYearStart, setHijriYearStart] = useState<number | null>(null);
  const [calendarData, setCalendarData] = useState<CalendarPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"calendar" | "table">("calendar");
  const [monthCursor, setMonthCursor] = useState(() => dayjs().startOf("month"));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [assignable, setAssignable] = useState<AssignableContribution[]>([]);
  const [addContributionId, setAddContributionId] = useState("");
  const [addMode, setAddMode] = useState<"SELF" | "VAKALAT">("SELF");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await callApi({
          location: "fmbReceipt",
          method: "GET",
          id: "dashboard/periods",
        });
        const list = (res?.data as { periods?: FmbDashboardPeriod[] } | undefined)?.periods ?? [];
        if (!cancelled) {
          setPeriods(list);
          if (list.length > 0) {
            setHijriYearStart(list[0].hijriYearStart);
          }
        }
      } catch (err) {
        notify(err instanceof Error ? err.message : "Failed to load periods", { type: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const loadCalendar = useCallback(async () => {
    if (hijriYearStart == null) return;
    setLoading(true);
    try {
      const { json } = await httpClient(
        `${getApiUrl()}/fmbZabihatBookedSlots/calendar?hijriYearStart=${hijriYearStart}`
      );
      setCalendarData(json as CalendarPayload);
    } catch {
      notify("Error loading zabihat calendar", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [hijriYearStart, notify]);

  const loadAssignable = useCallback(async () => {
    if (hijriYearStart == null) return;
    try {
      const { json } = await httpClient(
        `${getApiUrl()}/fmbZabihatBookedSlots/assignable?hijriYearStart=${hijriYearStart}`
      );
      setAssignable((json as { rows?: AssignableContribution[] })?.rows ?? []);
    } catch {
      setAssignable([]);
    }
  }, [hijriYearStart]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const daysByDate = useMemo(() => {
    const map = new Map<string, DayPayload>();
    for (const d of calendarData?.days ?? []) {
      map.set(d.bookedDate, d);
    }
    return map;
  }, [calendarData]);

  const monthDays = useMemo(() => {
    const start = monthCursor.startOf("month");
    const daysInMonth = start.daysInMonth();
    const cells: Array<{ date: string; dayNum: number; inMonth: true } | null> = [];
    const startWeekday = start.day(); // 0 Sun
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      const date = start.date(d);
      cells.push({ date: formatIsoDate(date.toDate()), dayNum: d, inMonth: true });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthCursor]);

  const weekCount = monthDays.length / 7;

  const todayIso = formatIsoDate(new Date());

  const selectedDay = selectedDate ? daysByDate.get(selectedDate) : undefined;
  const selectedBookings = selectedDay?.bookings ?? [];

  const selectedAssignable = useMemo(
    () => assignable.find((c) => c.id === addContributionId) ?? null,
    [assignable, addContributionId]
  );

  const openDay = async (date: string) => {
    setSelectedDate(date);
    setAddContributionId("");
    setAddMode("SELF");
    await loadAssignable();
  };

  const handleDelete = async (bookingId: string) => {
    try {
      await httpClient(`${getApiUrl()}/fmbZabihatBookedSlots/${bookingId}`, {
        method: "DELETE",
      });
      notify("Zabihat date removed", { type: "info" });
      await loadCalendar();
      await loadAssignable();
    } catch {
      notify("Failed to remove booking", { type: "error" });
    }
  };

  const handleAdd = async () => {
    if (!selectedDate || !addContributionId || hijriYearStart == null) return;
    setAdding(true);
    try {
      await httpClient(`${getApiUrl()}/fmbZabihatBookedSlots`, {
        method: "POST",
        body: JSON.stringify({
          fmbContributionId: addContributionId,
          bookedDate: selectedDate,
          performanceMode: addMode,
          hijriYearStart,
        }),
      });
      notify("Zabihat date assigned", { type: "info" });
      await loadCalendar();
      setAddContributionId("");
      setSelectedDate(null);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "body" in err
          ? String((err as { body?: { message?: string } }).body?.message ?? "Failed to add")
          : "Failed to add booking";
      notify(msg, { type: "error" });
    } finally {
      setAdding(false);
    }
  };

  const tableRows = useMemo(
    () => [...(calendarData?.days ?? [])].sort((a, b) => a.bookedDate.localeCompare(b.bookedDate)),
    [calendarData]
  );

  if (hijriYearStart == null && periods.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Title title="Zabihat Booked Slots" />
        <Typography>No FMB periods found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        height: "calc(100dvh - 48px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: viewMode === "calendar" ? "hidden" : "auto",
      }}
    >
      <Title title="Zabihat Booked Slots" />
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)}>
            <Tab value="calendar" label="Calendar" />
            <Tab value="table" label="Table" />
          </Tabs>
          <RaButton label="Refresh" onClick={() => void loadCalendar()}>
            <RefreshIcon />
          </RaButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {PERFORMANCE_LEGEND.map((item) => {
              const colors = performanceModeSx(theme, item.mode);
              return (
                <Box key={item.mode} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: colors.bgcolor,
                      border: 1,
                      borderColor: colors.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: "auto",
              flexWrap: "wrap",
            }}
          >
            <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
              <InputLabel id="fmb-zabihat-period-label">Period</InputLabel>
              <Select
                labelId="fmb-zabihat-period-label"
                label="Period"
                value={hijriYearStart != null ? String(hijriYearStart) : ""}
                onChange={(e) => {
                  setHijriYearStart(Number.parseInt(String(e.target.value), 10));
                  setSelectedDate(null);
                }}
                disabled={!periods.length}
              >
                {periods.map((p) => (
                  <MenuItem key={p.hijriYearStart} value={String(p.hijriYearStart)}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {viewMode === "calendar" ? (
              <>
                <Button size="small" onClick={() => setMonthCursor((m) => m.subtract(1, "month"))}>
                  Prev
                </Button>
                <Typography variant="subtitle1">
                  {monthCursor.format(MUI_MONTH_YEAR_PICKER_FORMAT)}
                </Typography>
                <Button size="small" onClick={() => setMonthCursor((m) => m.add(1, "month"))}>
                  Next
                </Button>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>

      {loading ? <Typography>Loading…</Typography> : null}

      {!loading && viewMode === "calendar" ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: 1,
              mb: 0.5,
              flexShrink: 0,
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <Typography key={d} variant="caption" color="text.secondary" sx={{ px: 1 }}>
                {d}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gridTemplateRows: `repeat(${weekCount}, minmax(0, 1fr))`,
              gap: 1,
            }}
          >
            {monthDays.map((cell, idx) => {
              if (!cell) {
                return <Box key={`empty-${idx}`} sx={{ minWidth: 0, height: "100%" }} />;
              }
              const dayData = daysByDate.get(cell.date);
              const bookings = dayData?.bookings ?? [];
              const count = dayData?.count ?? bookings.length;
              const d = dayjs(cell.date).toDate();
              const isToday = cell.date === todayIso;
              const hijriLong = fromGregorian(d, "long");
              const gregorianLabel = formatListDate(d, { empty: "—" });
              const visibleBookings = bookings.slice(0, MAX_DAY_ASSIGNMENTS_VISIBLE);
              const hiddenCount = Math.max(0, bookings.length - visibleBookings.length);
              const assignmentTooltip = bookings
                .map((b) => `${bookingLabel(b)} · ${modeLabel(b.performanceMode)}`)
                .join("\n");
              return (
                <Paper
                  key={cell.date}
                  elevation={0}
                  onClick={() => void openDay(cell.date)}
                  title={[
                    `${gregorianLabel} · ${hijriLong}`,
                    assignmentTooltip || "No assignments",
                  ].join("\n")}
                  sx={{
                    p: 0.75,
                    height: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: 2,
                    borderColor: isToday ? "primary.main" : "divider",
                    bgcolor:
                      count > 0 ? alpha(theme.palette.primary.main, 0.12) : "background.paper",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={isToday ? "primary.main" : "text.primary"}
                      fontWeight={isToday ? 700 : 600}
                      noWrap
                      sx={{ lineHeight: 1.2, minWidth: 0 }}
                    >
                      {cell.dayNum}
                      {isToday ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="primary.main"
                          sx={{ ml: 0.5, fontWeight: 600 }}
                        >
                          Today
                        </Typography>
                      ) : null}
                    </Typography>
                    {count > 0 ? (
                      <Typography
                        variant="caption"
                        color="primary.main"
                        fontWeight={700}
                        sx={{ lineHeight: 1.2, flexShrink: 0 }}
                      >
                        {count}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", lineHeight: 1.25, flexShrink: 0 }}
                  >
                    {gregorianLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", lineHeight: 1.25, flexShrink: 0 }}
                  >
                    {hijriLong}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.25,
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                    }}
                  >
                    {visibleBookings.map((b) => {
                      const colors = performanceModeSx(theme, b.performanceMode);
                      const { name, its } = compactAssignmentParts(b);
                      return (
                        <Box
                          key={b.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            px: 0.5,
                            py: 0.125,
                            borderRadius: 0.5,
                            color: colors.color,
                            bgcolor: colors.bgcolor,
                            flexShrink: 0,
                          }}
                        >
                          {name ? (
                            <Typography
                              variant="caption"
                              component="span"
                              noWrap
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 600,
                                color: "inherit",
                                lineHeight: 1.2,
                              }}
                            >
                              {name}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              flexShrink: 0,
                              fontWeight: 600,
                              color: "inherit",
                              lineHeight: 1.2,
                            }}
                          >
                            {its}
                          </Typography>
                        </Box>
                      );
                    })}
                    {hiddenCount > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        +{hiddenCount} more
                      </Typography>
                    ) : null}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {!loading && viewMode === "table" ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Hijri</TableCell>
                <TableCell>Count</TableCell>
                <TableCell>Bookings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary">No zabihat dates assigned yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row) => (
                  <TableRow
                    key={row.bookedDate}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => void openDay(row.bookedDate)}
                  >
                    <TableCell>{formatListDate(row.bookedDate, { empty: "—" })}</TableCell>
                    <TableCell>{fromGregorian(dayjs(row.bookedDate).toDate(), "long")}</TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {row.bookings.map((b) => (
                          <Chip
                            key={b.id}
                            size="small"
                            label={`${bookingLabel(b)} · ${modeLabel(b.performanceMode)}`}
                            color={b.performanceMode === "VAKALAT" ? "secondary" : "info"}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Dialog
        open={Boolean(selectedDate)}
        onClose={() => setSelectedDate(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedDate
            ? `${formatListDate(selectedDate, { empty: "—" })} · ${fromGregorian(
                dayjs(selectedDate).toDate(),
                "long"
              )}`
            : "Day"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedBookings.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No zabihats assigned on this day.
            </Typography>
          ) : (
            selectedBookings.map((b) => (
              <Box
                key={b.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <Link to={`/fmbContributions/${b.fmbContributionId}`}>{bookingLabel(b)}</Link>
                  </Typography>
                  <Chip
                    size="small"
                    label={modeLabel(b.performanceMode)}
                    color={b.performanceMode === "VAKALAT" ? "secondary" : "info"}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <IconButton
                  aria-label="Remove"
                  color="error"
                  onClick={() => void handleDelete(b.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Assign zabihat
          </Typography>
          <Autocomplete
            options={assignable}
            value={selectedAssignable}
            onChange={(_, value) => setAddContributionId(value?.id ?? "")}
            getOptionLabel={assignableOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(options, state) => filterAssignableOptions(options, state.inputValue)}
            noOptionsText={
              assignable.length === 0
                ? "No contributions with remaining quantity"
                : "No matching ITS / name / file"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search contribution by ITS"
                size="small"
                helperText="Type beneficiary or HOF ITS, name, or file no."
              />
            )}
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="assign-mode">Mode</InputLabel>
            <Select
              labelId="assign-mode"
              label="Mode"
              value={addMode}
              onChange={(e) => setAddMode(e.target.value as "SELF" | "VAKALAT")}
            >
              <MenuItem value="SELF">Self</MenuItem>
              <MenuItem value="VAKALAT">Vakalat</MenuItem>
            </Select>
          </FormControl>
          {selectedDate ? (
            <TextField
              label="Date"
              value={selectedDate}
              fullWidth
              size="small"
              disabled
              sx={{ mb: 1 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDate(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!addContributionId || adding}
            onClick={() => void handleAdd()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FmbZabihatBookedSlotsList;
